// App.js 🧠✨ Toastified
import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify"; // ✅ Toastify import
import { destroyAllTooltips, watchTooltips } from "./components/utils/tooltipUtils";
import "react-toastify/dist/ReactToastify.css";  // ✅ Toastify CSS
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

import LoginForm from "./components/LoginForm.jsx";
import IocTable from "./components/IocTable.jsx";
import Header from "./components/Header.jsx";
import CertTable from "./components/CertTable.jsx";
import Footer from "./components/Footer.jsx";
import Profile from "./components/Profile.jsx";
import Posture from "./components/stats/Posture.jsx";
import Threats from "./components/stats/Threats.jsx";
import RiskTable from "./components/risks/RiskTable.jsx";
import Inventory from "./components/inventory/Inventory.jsx";
import Policies from "./components/policies/Policies.jsx";
import Audit from "./components/audit/AuditTable.jsx";
import Tools from "./components/tools/Tools.jsx";
import Kpi from "./components/kpi/Kpi.jsx";

// ✅ Admin imports
import Settings from "./components/admin/Settings.jsx";
import RequireAdmin from "./components/routing/RequireAdmin.jsx";

export default function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleLogout = async () => {
    await fetch("/api/logout.php", {
      method: "POST",
      credentials: "include",
    });
    setUser(null);
  };

  useEffect(() => {
    fetch("/api/me.php", { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        if (data.user) setUser(data.user);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    const stopWatching = watchTooltips(); // 🔄 auto-refresh tooltips on DOM change
    return () => stopWatching();          // 🧼 cleanup on unmount
  }, []);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center bg-dark text-light vh-100">
        Loading...
      </div>
    );
  }

  return (
    <BrowserRouter basename="/">
      {user && <Header user={user} onLogout={handleLogout} />}

      <Routes>
        <Route
          path="/login"
          element={!user ? <LoginForm setUser={setUser} /> : <Navigate to="/iocs" />}
        />

        <Route
          path="/iocs"
          element={user ? <IocTable userCountryCode={user.location} /> : <Navigate to="/login" />}
        />

        <Route
          path="/cert"
          element={user ? <CertTable userCountryCode={user.location} /> : <Navigate to="/login" />}
        />

        <Route
          path="/"
          element={<Navigate to={user ? "/iocs" : "/login"} />}
        />

        <Route
          path="/profile"
          element={user ? <Profile user={user} setUser={setUser} /> : <Navigate to="/login" />}
        />

        <Route
          path="/stats/posture"
          element={user ? <Posture user={user} /> : <Navigate to="/login" />}
        />

        <Route
          path="/stats/threats"
          element={user ? <Threats user={user} /> : <Navigate to="/login" />}
        />

        <Route
          path="/risks"
          element={user ? <RiskTable userCountryCode={user.location} /> : <Navigate to="/login" />}
        />

        <Route
          path="/inventory"
          element={user ? <Inventory userCountryCode={user.location} /> : <Navigate to="/login" />}
        />

        <Route
          path="/policies"
          element={user ? <Policies userCountryCode={user.location} /> : <Navigate to="/login" />}
        />

        <Route
          path="/audit"
          element={user ? <Audit user={user} /> : <Navigate to="/login" />}
        />
        <Route
          path="/tools"
          element={user ? <Tools /> : <Navigate to="/login" />}
        />
		<Route
          path="/kpi"
          element={user ? <Kpi user={user} /> : <Navigate to="/login" />}
        />

        {/* ✅ Admin-only route */}
        <Route
          path="/settings"
          element={
            user ? (
              <RequireAdmin user={user}>
                <Settings user={user} />
              </RequireAdmin>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>

      {/* ✅ Global Footer */}
      <Footer />

      {/* ✅ Global Toast Container */}
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        style={{
          position: "fixed",
          zIndex: 9999,
          bottom: "20px",
          right: "20px",
        }}
      />
    </BrowserRouter>
  );
}
